/**
 * SHARED NON-DISMISSIBLE WITHDRAWAL EMAIL VERIFICATION MODAL
 *
 * Reusable overlay modal shown after a withdrawal request requiring email verification.
 * Used by both CommissionWithdrawal.tsx and ComprehensiveAffiliateManager.tsx.
 *
 * Behavior:
 * - Fixed fullscreen overlay (z-50)
 * - Non-dismissible while waiting (no close/back/cancel, Escape blocked)
 * - Polls /api/user/withdrawal-status every 5 seconds
 * - BroadcastChannel listener for cross-tab verification notifications
 * - Multilingual instructions (EN / FR / HI)
 * - Auto-opens email inbox once after 3 seconds
 * - Auto-closes 5 seconds after verification success
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';
import { EMAIL_PROVIDERS, attemptOpenEmailInbox } from '../../lib/emailProviderHelper';

// Helper to mask an email address for privacy (e.g. "j***@gmail.com")
const maskEmail = (email: string): string => {
  if (!email || !email.includes('@')) return '***@***.***';
  const [local, domain] = email.split('@');
  if (local.length <= 1) return `${local}***@${domain}`;
  return `${local[0]}***@${domain}`;
};

// Format seconds into MM:SS
const formatCountdown = (seconds: number | null): string => {
  if (seconds === null || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const secondsUntilExpiry = (expiresAt?: string | null): number | null => {
  if (!expiresAt) return null;
  const expiryMs = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiryMs)) return null;
  return Math.max(0, Math.floor((expiryMs - Date.now()) / 1000));
};

export interface WithdrawalConfirmationData {
  withdrawalId: string;
  amount: number;
  walletAddress: string;
  network: string;
  linkSent: boolean;
  expiresAt?: string | null;
}

interface WithdrawalEmailVerificationModalProps {
  confirmationData: WithdrawalConfirmationData;
  userEmail: string;
  onClose: () => void; // Called when modal should close (after verified/expired user action or auto-close)
  onDataRefresh?: () => void; // Optional callback to refresh withdrawal data after close
}

export const WithdrawalEmailVerificationModal: React.FC<WithdrawalEmailVerificationModalProps> = ({
  confirmationData,
  userEmail,
  onClose,
  onDataRefresh,
}) => {
  const [verificationStatus, setVerificationStatus] = useState<'waiting' | 'verified' | 'expired'>('waiting');
  const [authoritativeExpiresAt, setAuthoritativeExpiresAt] = useState<string | null>(confirmationData.expiresAt ?? null);
  const [expirySecondsLeft, setExpirySecondsLeft] = useState<number | null>(() => secondsUntilExpiry(confirmationData.expiresAt) ?? 15 * 60);
  const [_emailAutoOpened, setEmailAutoOpened] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Poll withdrawal verification status
  const pollWithdrawalStatus = useCallback(async () => {
    try {
      const sessionHeaders = getSessionHeaders();
      const res = await fetch('/api/user/withdrawal-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...sessionHeaders },
        body: JSON.stringify({ withdrawalId: confirmationData.withdrawalId })
      });
      if (!res.ok) return;
      const result = await res.json();
      if (!result.success || !result.data) return;

      const { status, verified_by_email, expired, expires_at } = result.data;

      if (verified_by_email || status === 'pending') {
        setVerificationStatus('verified');
        if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
      } else if (expired || status === 'cancelled') {
        setVerificationStatus('expired');
        if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
      } else if (expires_at) {
        setAuthoritativeExpiresAt(expires_at);
        const secondsLeft = secondsUntilExpiry(expires_at) ?? 0;
        setExpirySecondsLeft(secondsLeft);
        if (secondsLeft <= 0) {
          setVerificationStatus('expired');
          if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
          if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
        }
      }
    } catch {
      // Ignore polling errors silently
    }
  }, [confirmationData.withdrawalId]);

  // Start polling, countdown, auto-open, and BroadcastChannel on mount
  useEffect(() => {
    // Initial poll
    pollWithdrawalStatus();

    // Poll every 5 seconds
    pollingRef.current = setInterval(pollWithdrawalStatus, 5000);

    // Countdown timer every second
    countdownRef.current = setInterval(() => {
      if (authoritativeExpiresAt) {
        setExpirySecondsLeft(secondsUntilExpiry(authoritativeExpiresAt) ?? 0);
        return;
      }
      setExpirySecondsLeft(prev => {
        if (prev === null || prev <= 0) return 0;
        return prev - 1;
      });
    }, 1000);

    // Auto-open email inbox after 3 seconds (once)
    if (confirmationData.linkSent && userEmail) {
      autoOpenTimerRef.current = setTimeout(() => {
        attemptOpenEmailInbox(userEmail);
        setEmailAutoOpened(true);
      }, 3000);
    }

    // BroadcastChannel listener
    let broadcastChannel: BroadcastChannel | null = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        broadcastChannel = new BroadcastChannel('aureus_withdrawal_verification');
        broadcastChannel.onmessage = (event) => {
          if (event.data?.type === 'withdrawal_verified') {
            setVerificationStatus('verified');
            if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
            if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
          } else if (event.data?.type === 'withdrawal_verification_failed') {
            pollWithdrawalStatus();
          }
        };
      }
    } catch { /* BroadcastChannel not supported */ }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (autoOpenTimerRef.current) clearTimeout(autoOpenTimerRef.current);
      if (broadcastChannel) { try { broadcastChannel.close(); } catch {} }
    };
  }, [confirmationData.withdrawalId, authoritativeExpiresAt]);

  // Block Escape key while in waiting state
  useEffect(() => {
    if (verificationStatus !== 'waiting') return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [verificationStatus]);

  // Auto-close modal 5 seconds after verification success
  useEffect(() => {
    if (verificationStatus !== 'verified') return;
    const timer = setTimeout(() => {
      if (onDataRefresh) onDataRefresh();
      onClose();
    }, 5000);
    return () => clearTimeout(timer);
  }, [verificationStatus]);

  // Handle resending verification email
  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const sessionHeaders = getSessionHeaders();
      const response = await fetch('/api/resend-withdrawal-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...sessionHeaders },
        body: JSON.stringify({ withdrawalId: confirmationData.withdrawalId })
      });
      const result = await response.json();
      if (result.success) {
        alert('✅ A new verification link has been sent to your email. Please check your inbox and click the link to verify your withdrawal.');
      } else {
        alert(`❌ Failed to resend verification: ${result.error}`);
      }
    } catch {
      alert('❌ Failed to resend verification. Please try again.');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleCloseAction = () => {
    if (onDataRefresh) onDataRefresh();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="bg-gray-900 border-2 border-yellow-500 rounded-xl p-6 shadow-2xl shadow-yellow-500/20 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >

      {/* ── VERIFIED STATE ── */}
      {verificationStatus === 'verified' && (
        <>
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
              <span className="text-5xl">✅</span>
            </div>
          </div>
          <h3 className="text-xl font-bold text-green-400 text-center mb-2">Withdrawal Verified Successfully</h3>
          <p className="text-center text-gray-300 mb-4">
            Your withdrawal request is now ready for admin review. You will receive an email once it is processed.
          </p>
          <p className="text-center text-gray-500 text-xs mb-4">This window will close automatically in a few seconds.</p>
          <div className="bg-gray-800/60 rounded-lg p-4 mb-4 border border-gray-700">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-white">Amount:</span>
              <span className="text-white font-semibold text-right">${confirmationData.amount.toFixed(2)} USDT</span>
              <span className="text-white">Status:</span>
              <span className="text-green-400 font-semibold text-right">Verified — Pending Admin Review</span>
            </div>
          </div>
          <button
            onClick={handleCloseAction}
            className="w-full py-3 px-4 rounded-lg font-semibold transition-colors text-sm bg-green-600 hover:bg-green-700 text-white"
          >
            ← Return to Dashboard
          </button>
        </>
      )}

      {/* ── EXPIRED STATE ── */}
      {verificationStatus === 'expired' && (
        <>
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
              <span className="text-5xl">⏰</span>
            </div>
          </div>
          <h3 className="text-xl font-bold text-red-400 text-center mb-2">Verification Expired</h3>
          <p className="text-center text-gray-300 mb-4">
            Your withdrawal was cancelled and funds were released back to your balance. Please submit a new withdrawal request.
          </p>
          <button
            onClick={handleCloseAction}
            className="w-full py-3 px-4 rounded-lg font-semibold transition-colors text-sm bg-yellow-600 hover:bg-yellow-700 text-black"
          >
            ← Submit New Withdrawal
          </button>
        </>
      )}

      {/* ── WAITING STATE (non-dismissible) ── */}
      {verificationStatus === 'waiting' && (
        <>
          {/* Email Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <svg className="w-10 h-10 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
          </div>

          {/* Heading */}
          <h3 className="text-xl font-bold text-yellow-400 text-center mb-2">
            {confirmationData.linkSent
              ? '📧 Verify Your Withdrawal via Email'
              : '⚠️ Email Could Not Be Sent'}
          </h3>

          {/* Non-dismissible warning */}
          <div className="bg-amber-900/40 border border-amber-500/60 rounded-lg p-3 mb-4">
            <p className="text-amber-200 text-xs text-center font-semibold">
              🔒 This window cannot be closed until you verify your withdrawal or the link expires.
            </p>
          </div>

          {/* Multilingual instructions */}
          <div className="space-y-3 mb-4">
            {/* English */}
            <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700">
              <p className="text-xs text-gray-400 mb-1 font-semibold">🇬🇧 English</p>
              <p className="text-gray-200 text-sm">
                Your withdrawal request has been created. To continue, open your email inbox and click the withdrawal verification link we sent you. Your withdrawal will not be sent for admin review until you verify it. This link expires in 15 minutes.
              </p>
            </div>
            {/* French */}
            <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700">
              <p className="text-xs text-gray-400 mb-1 font-semibold">🇫🇷 Français</p>
              <p className="text-gray-200 text-sm">
                {"Votre demande de retrait a été créée. Pour continuer, ouvrez votre boîte e-mail et cliquez sur le lien de vérification du retrait que nous vous avons envoyé. Votre retrait ne sera pas envoyé pour examen par l\u2019administration tant que vous ne l\u2019aurez pas vérifié. Ce lien expire dans 15 minutes."}
              </p>
            </div>
            {/* Hindi */}
            <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700">
              <p className="text-xs text-gray-400 mb-1 font-semibold">🇮🇳 हिन्दी</p>
              <p className="text-gray-200 text-sm">
                {"आपका निकासी अनुरोध बना दिया गया है। आगे बढ़ने के लिए, अपनी ईमेल इनबॉक्स खोलें और हमारे द्वारा भेजे गए निकासी सत्यापन लिंक पर क्लिक करें। जब तक आप इसे सत्यापित नहीं करते, आपकी निकासी प्रशासनिक समीक्षा के लिए नहीं भेजी जाएगी। यह लिंक 15 मिनट में समाप्त हो जाएगा।"}
              </p>
            </div>
          </div>

          {/* Withdrawal details */}
          <div className="bg-gray-800/60 rounded-lg p-4 mb-4 border border-gray-700">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-white">Amount:</span>
              <span className="text-white font-semibold text-right">${confirmationData.amount.toFixed(2)} USDT</span>
              <span className="text-white">Network:</span>
              <span className="text-white text-right">{confirmationData.network}</span>
              <span className="text-white">Wallet:</span>
              <span className="text-white font-mono text-right text-sm truncate">
                {confirmationData.walletAddress.substring(0, 10)}...{confirmationData.walletAddress.slice(-6)}
              </span>
              {userEmail && (
                <>
                  <span className="text-white">Email:</span>
                  <span className="text-white text-right">{maskEmail(userEmail)}</span>
                </>
              )}
              <span className="text-white">Status:</span>
              <span className="text-yellow-400 text-right flex items-center justify-end gap-1">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-400"></div>
                Waiting for email verification
              </span>
            </div>
          </div>

          {/* Expiry countdown */}
          <div className="bg-red-900/30 border border-red-500/40 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-2">
                <span className="text-red-400 text-lg mt-0.5">⏰</span>
                <p className="text-red-300 text-sm">
                  <strong>Link expires in {formatCountdown(expirySecondsLeft)}</strong>
                </p>
              </div>
              {expirySecondsLeft !== null && expirySecondsLeft > 0 && (
                <span className="text-red-400 font-mono text-lg font-bold">{formatCountdown(expirySecondsLeft)}</span>
              )}
            </div>
          </div>

          {/* Email provider buttons */}
          <div className="bg-gray-800/40 rounded-lg p-4 mb-4">
            <p className="text-white text-sm text-center mb-3 font-medium">
              📬 Open your email inbox:
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {EMAIL_PROVIDERS.map(provider => (
                <a
                  key={provider.name}
                  href={provider.inboxUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-2 px-3 rounded-lg text-sm font-medium text-center transition-colors bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                >
                  {provider.name === 'Gmail' && '📧 '}
                  {provider.name === 'Outlook' && '📨 '}
                  {provider.name === 'Yahoo' && '📩 '}
                  {provider.name === 'iCloud' && '🍎 '}
                  Open {provider.name}
                </a>
              ))}
            </div>
            <p className="text-gray-400 text-xs text-center">
              Don't see your email provider? Open your email inbox and search for <strong className="text-white">"Aureus withdrawal verification"</strong>.
            </p>
          </div>

          {/* Info note */}
          <div className="bg-gray-800/40 rounded-lg p-3 mb-4">
            <p className="text-white text-sm text-center">
              📌 Didn't receive the email? Check your spam/junk folder or click "Resend Email" below.
            </p>
          </div>

          {/* Only Resend button — no close/back/cancel while waiting */}
          <button
            onClick={handleResendVerification}
            disabled={resendingVerification}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors text-sm ${
              resendingVerification
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-yellow-600 hover:bg-yellow-700 text-black font-semibold'
            }`}
          >
            {resendingVerification ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Sending...
              </span>
            ) : (
              '📧 Resend Email'
            )}
          </button>
        </>
      )}

      </div>
    </div>
  );
};
